import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format, parseISO, subDays } from "date-fns";
import {
  Activity,
  Heart,
  Flame,
  MapPin,
  Clock,
  Plus,
  Trash2,
  TrendingUp,
  ArrowLeft,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { Link } from "wouter";

const CARDIO_TYPES = [
  "Running",
  "Cycling",
  "Walking",
  "Rowing",
  "Swimming",
  "Elliptical",
  "Jump Rope",
  "Stair Climber",
  "HIIT",
  "Other",
];

function formatPace(paceMinPerMile: number | null | undefined) {
  if (!paceMinPerMile) return "—";
  const mins = Math.floor(paceMinPerMile);
  const secs = Math.round((paceMinPerMile - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, "0")}/mi`;
}

function formatDuration(minutes: number | null | undefined) {
  if (!minutes) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function CardioLog() {
  const { data: sessions = [], refetch } = trpc.cardio.list.useQuery();
  const saveMutation = trpc.cardio.save.useMutation({
    onSuccess: () => {
      toast.success("Cardio session saved!");
      refetch();
      setOpen(false);
      resetForm();
    },
    onError: (err) => toast.error(err.message),
  });
  const deleteMutation = trpc.cardio.delete.useMutation({
    onSuccess: () => {
      toast.success("Session deleted.");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    workoutType: "Running",
    durationMinutes: "",
    calories: "",
    avgHeartRate: "",
    maxHeartRate: "",
    distanceMiles: "",
    notes: "",
  });

  function resetForm() {
    setForm({
      date: format(new Date(), "yyyy-MM-dd"),
      workoutType: "Running",
      durationMinutes: "",
      calories: "",
      avgHeartRate: "",
      maxHeartRate: "",
      distanceMiles: "",
      notes: "",
    });
  }

  function handleSave() {
    if (!form.date || !form.workoutType) {
      toast.error("Date and workout type are required.");
      return;
    }
    saveMutation.mutate({
      date: form.date,
      workoutType: form.workoutType,
      durationMinutes: form.durationMinutes ? parseFloat(form.durationMinutes) : null,
      calories: form.calories ? parseFloat(form.calories) : null,
      avgHeartRate: form.avgHeartRate ? parseFloat(form.avgHeartRate) : null,
      maxHeartRate: form.maxHeartRate ? parseFloat(form.maxHeartRate) : null,
      distanceMiles: form.distanceMiles ? parseFloat(form.distanceMiles) : null,
      notes: form.notes || null,
    });
  }

  // Sort sessions newest first
  const sorted = useMemo(
    () => [...sessions].sort((a, b) => b.date.localeCompare(a.date)),
    [sessions]
  );

  // Last 30 days for charts
  const last30 = useMemo(() => {
    const cutoff = format(subDays(new Date(), 30), "yyyy-MM-dd");
    return [...sessions]
      .filter((s) => s.date >= cutoff)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [sessions]);

  const hrChartData = last30
    .filter((s) => s.avgHeartRate)
    .map((s) => ({
      date: format(parseISO(s.date), "M/d"),
      avgHR: s.avgHeartRate,
      maxHR: s.maxHeartRate,
    }));

  const calChartData = last30
    .filter((s) => s.calories)
    .map((s) => ({
      date: format(parseISO(s.date), "M/d"),
      calories: s.calories,
    }));

  const distChartData = last30
    .filter((s) => s.distanceMiles)
    .map((s) => ({
      date: format(parseISO(s.date), "M/d"),
      miles: s.distanceMiles,
    }));

  // Summary stats
  const totalSessions = sessions.length;
  const totalCalories = sessions.reduce((sum, s) => sum + (s.calories ?? 0), 0);
  const totalMiles = sessions.reduce((sum, s) => sum + (s.distanceMiles ?? 0), 0);
  const avgHR =
    sessions.filter((s) => s.avgHeartRate).length > 0
      ? Math.round(
          sessions.reduce((sum, s) => sum + (s.avgHeartRate ?? 0), 0) /
            sessions.filter((s) => s.avgHeartRate).length
        )
      : null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/60 backdrop-blur-md sticky top-0 z-40" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="container py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-red-500/20 flex items-center justify-center rounded-sm">
                <Activity className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h1 className="font-mono text-base font-bold tracking-wider text-primary">
                  FORGE // CARDIO
                </h1>
                <p className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest">
                  CARDIO SESSION LOG
                </p>
              </div>
            </div>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="font-mono text-xs gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                LOG CARDIO
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-mono">LOG CARDIO SESSION</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono">DATE</Label>
                    <Input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono">TYPE</Label>
                    <Select
                      value={form.workoutType}
                      onValueChange={(v) => setForm((f) => ({ ...f, workoutType: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CARDIO_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono">DURATION (min)</Label>
                    <Input
                      type="number"
                      placeholder="45"
                      value={form.durationMinutes}
                      onChange={(e) => setForm((f) => ({ ...f, durationMinutes: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono">CALORIES</Label>
                    <Input
                      type="number"
                      placeholder="350"
                      value={form.calories}
                      onChange={(e) => setForm((f) => ({ ...f, calories: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono">AVG HR (bpm)</Label>
                    <Input
                      type="number"
                      placeholder="145"
                      value={form.avgHeartRate}
                      onChange={(e) => setForm((f) => ({ ...f, avgHeartRate: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono">MAX HR (bpm)</Label>
                    <Input
                      type="number"
                      placeholder="172"
                      value={form.maxHeartRate}
                      onChange={(e) => setForm((f) => ({ ...f, maxHeartRate: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-mono">DISTANCE (miles)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="3.1"
                    value={form.distanceMiles}
                    onChange={(e) => setForm((f) => ({ ...f, distanceMiles: e.target.value }))}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-mono">NOTES</Label>
                  <Textarea
                    placeholder="How'd it feel?"
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={2}
                  />
                </div>

                <Button
                  className="w-full font-mono"
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? "SAVING..." : "SAVE SESSION"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="container py-6 space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-card/60 border-border">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="h-4 w-4 text-primary" />
                <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Sessions</span>
              </div>
              <p className="text-2xl font-bold font-mono text-primary">{totalSessions}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/60 border-border">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-1">
                <Flame className="h-4 w-4 text-orange-400" />
                <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Total Cal</span>
              </div>
              <p className="text-2xl font-bold font-mono text-orange-400">
                {totalCalories > 0 ? totalCalories.toLocaleString() : "—"}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/60 border-border">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="h-4 w-4 text-blue-400" />
                <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Total Miles</span>
              </div>
              <p className="text-2xl font-bold font-mono text-blue-400">
                {totalMiles > 0 ? totalMiles.toFixed(1) : "—"}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/60 border-border">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 mb-1">
                <Heart className="h-4 w-4 text-red-400" />
                <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Avg HR</span>
              </div>
              <p className="text-2xl font-bold font-mono text-red-400">
                {avgHR ? `${avgHR} bpm` : "—"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        {sessions.length > 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hrChartData.length > 1 && (
              <Card className="bg-card/60 border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Heart className="h-3.5 w-3.5 text-red-400" />
                    HEART RATE TREND (30 DAYS)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={hrChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "4px",
                          fontSize: "11px",
                        }}
                      />
                      <Line type="monotone" dataKey="avgHR" stroke="#f87171" strokeWidth={2} dot={false} name="Avg HR" />
                      <Line type="monotone" dataKey="maxHR" stroke="#fca5a5" strokeWidth={1} dot={false} strokeDasharray="4 2" name="Max HR" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {calChartData.length > 1 && (
              <Card className="bg-card/60 border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Flame className="h-3.5 w-3.5 text-orange-400" />
                    CALORIES BURNED (30 DAYS)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={calChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "4px",
                          fontSize: "11px",
                        }}
                      />
                      <Bar dataKey="calories" fill="#fb923c" name="Calories" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {distChartData.length > 1 && (
              <Card className="bg-card/60 border-border md:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <TrendingUp className="h-3.5 w-3.5 text-blue-400" />
                    DISTANCE TREND (30 DAYS)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={140}>
                    <LineChart data={distChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "4px",
                          fontSize: "11px",
                        }}
                      />
                      <Line type="monotone" dataKey="miles" stroke="#60a5fa" strokeWidth={2} dot={false} name="Miles" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Session Log */}
        <div>
          <h2 className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-3">
            SESSION HISTORY ({sessions.length})
          </h2>

          {sorted.length === 0 ? (
            <Card className="bg-card/40 border-dashed border-border">
              <CardContent className="py-12 text-center">
                <Activity className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm font-mono text-muted-foreground">NO CARDIO SESSIONS LOGGED</p>
                <p className="text-xs text-muted-foreground/60 mt-1">
                  Log manually or import from Apple Watch screenshots
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {sorted.map((session) => (
                <Card
                  key={session.id}
                  className="bg-card/60 border-border hover:border-primary/30 transition-colors"
                >
                  <CardContent className="py-3 px-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-semibold text-foreground">
                            {session.workoutType}
                          </span>
                          <span className="text-[10px] font-mono text-muted-foreground">
                            {format(parseISO(session.date), "MMM d, yyyy")}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-3 mt-1.5">
                          {session.durationMinutes && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {formatDuration(session.durationMinutes)}
                            </span>
                          )}
                          {session.calories && (
                            <span className="flex items-center gap-1 text-xs text-orange-400">
                              <Flame className="h-3 w-3" />
                              {session.calories} cal
                            </span>
                          )}
                          {session.avgHeartRate && (
                            <span className="flex items-center gap-1 text-xs text-red-400">
                              <Heart className="h-3 w-3" />
                              {session.avgHeartRate}
                              {session.maxHeartRate ? `/${session.maxHeartRate}` : ""} bpm
                            </span>
                          )}
                          {session.distanceMiles && (
                            <span className="flex items-center gap-1 text-xs text-blue-400">
                              <MapPin className="h-3 w-3" />
                              {session.distanceMiles} mi
                            </span>
                          )}
                          {session.avgPaceMinPerMile && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                              <TrendingUp className="h-3 w-3" />
                              {formatPace(session.avgPaceMinPerMile)}
                            </span>
                          )}
                        </div>

                        {session.notes && (
                          <p className="text-xs text-muted-foreground/70 mt-1 truncate">{session.notes}</p>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => {
                          if (confirm("Delete this cardio session?")) {
                            deleteMutation.mutate({ sessionId: session.id });
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
